import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { VaultItem } from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { formatNumber, formatRelativeTime } from "../lib/format.js";
import { htmlToText } from "./htmlToText.js";

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
  onRestored: () => Promise<void>;
}

const TYPE_ICON: Record<string, IconName> = {
  scene: "file",
  chapter: "layers",
  text: "note"
};

export function VaultPanel({ projectId, onOpenScene, onRestored }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<VaultItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<VaultItem | null>(null);

  const load = useCallback(async () => {
    const { items: list } = await api.listVault(projectId);
    setItems(list);
    setSelectedId((cur) => cur ?? list[0]?.id ?? null);
  }, [projectId]);

  useEffect(() => {
    load().catch((err) => toast.error(err instanceof Error ? err.message : "Failed to load vault"));
  }, [load, toast]);

  const selected = items?.find((i) => i.id === selectedId) ?? null;

  const restore = async (item: VaultItem) => {
    try {
      const res = await api.restoreVaultItem(item.id);
      toast.success(`${item.entityType === "chapter" ? "Chapter" : "Scene"} restored.`);
      setItems((prev) => (prev ? prev.filter((i) => i.id !== item.id) : prev));
      await onRestored();
      if (res.sceneId) onOpenScene(res.sceneId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Restore failed");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.deleteVaultItem(id);
      setItems((prev) => {
        const next = prev ? prev.filter((i) => i.id !== id) : prev;
        if (selectedId === id) setSelectedId(next?.[0]?.id ?? null);
        return next;
      });
      toast.success("Permanently deleted.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  return (
    <div className="collection">
      <div className="collection-list">
        <div className="collection-list-head">
          <span className="tree-toolbar-title" style={{ padding: "4px 6px" }}>Vault</span>
        </div>
        <div className="collection-items">
          {items === null ? (
            <div className="collection-loading"><Icon name="clock" size={16} className="spin" /></div>
          ) : items.length === 0 ? (
            <div className="collection-empty-list">
              <p className="muted">The Vault is empty.</p>
              <p className="faint" style={{ fontSize: 11.5 }}>Deleted scenes and chapters land here and can be restored.</p>
            </div>
          ) : (
            items.map((item) => (
              <button key={item.id} className={`collection-item ${item.id === selectedId ? "active" : ""}`} onClick={() => setSelectedId(item.id)}>
                <Icon name={TYPE_ICON[item.entityType] ?? "note"} size={14} className="collection-item-icon" />
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="truncate strong" style={{ fontSize: 13 }}>{item.title}</div>
                  <div className="truncate faint" style={{ fontSize: 11 }}>
                    {item.entityType} · {formatNumber(item.wordCount)} words · {formatRelativeTime(item.createdAt)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="collection-detail">
        {selected ? (
          <div className="detail">
            <header className="detail-head">
              <div className="col" style={{ minWidth: 0 }}>
                <h2 className="truncate" style={{ fontSize: 18 }}>{selected.title}</h2>
                <span className="faint" style={{ fontSize: 12 }}>
                  {selected.entityType} · {formatNumber(selected.wordCount)} words · deleted {formatRelativeTime(selected.createdAt)}
                </span>
              </div>
              <div className="row gap-2">
                {(selected.entityType === "scene" || selected.entityType === "chapter") && (
                  <button className="btn btn-primary btn-sm" onClick={() => restore(selected)}>
                    <Icon name="history" size={14} /> Restore
                  </button>
                )}
                <button className="btn btn-danger btn-sm" onClick={() => setPendingDelete(selected)}>
                  <Icon name="trash" size={14} /> Delete forever
                </button>
              </div>
            </header>
            <div className="detail-body">
              <div className="vault-preview">
                {(selected.content ?? "").trim()
                  ? htmlToText(selected.content ?? "").split("\n").map((line, i) => <p key={i}>{line || " "}</p>)
                  : <p className="muted">(no text content)</p>}
              </div>
            </div>
          </div>
        ) : (
          <div className="collection-detail-empty">
            <Icon name="vault" size={40} style={{ color: "var(--text-faint)" }} />
            <h2>Vault</h2>
            <p className="muted">Recoverable deleted material. Nothing here is gone until you delete it forever.</p>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete forever"
          message={`Permanently delete “${pendingDelete.title}”? This is the one action that cannot be undone.`}
          confirmLabel="Delete forever"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
